import { FormEvent, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/lib/auth-context'
import { coreApi } from '@/lib/coreApi'

const CORE_API = import.meta.env.VITE_CORE_API_URL || ''

interface PoolNominee {
  id: string
  type: 'teacher' | 'prospect'
  name: string
  detail: string | null
  weight: number
  support_code: string | null
  status: string
  coach_user_id?: string
}

interface Ceremony {
  id: string
  kind: string
  slots_available: number
  scheduled_at: string
  status: string
}

interface DrawnEntity {
  type: 'classroom' | 'locker'
  id: string
  portal_path: string
}

interface PoolData {
  pool: PoolNominee[]
  my_prospects: PoolNominee[]
  my_nomination: PoolNominee | null
  upcoming_ceremony: Ceremony | null
  drawn_entity: DrawnEntity | null
}

interface RallyPin {
  id: string
  display_name: string
  school_or_sport: string | null
  message: string
  pin_color: string
  nominee_type: string
  support_code: string
  weight: number
  posted_at: string
  expires_at: string
}

function formatDate(d: string) {
  try {
    return new Date(d).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
  } catch { return d }
}

function useCountdown(targetIso: string | null): string {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!targetIso) return
    const t = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(t)
  }, [targetIso])
  if (!targetIso) return ''
  const diff = new Date(targetIso).getTime() - now
  if (diff <= 0) return 'Drawing now'
  const days = Math.floor(diff / 86_400_000)
  const hours = Math.floor((diff % 86_400_000) / 3_600_000)
  if (days > 0) return `${days}d ${hours}h`
  const minutes = Math.floor((diff % 3_600_000) / 60_000)
  return `${hours}h ${minutes}m`
}

export default function NomineePortalHome() {
  const { profile } = useAuth()
  const role = (profile?.role || '').toLowerCase()
  const isCoach = role === 'coach' || role === 'admin' || role === 'superadmin'
  const isTeacher = role === 'teacher'

  const [data, setData] = useState<PoolData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [recentPins, setRecentPins] = useState<RallyPin[]>([])

  // Add prospect form
  const [showAddForm, setShowAddForm] = useState(false)
  const [addName, setAddName] = useState('')
  const [addEmail, setAddEmail] = useState('')
  const [addSport, setAddSport] = useState('')
  const [addSubmitting, setAddSubmitting] = useState(false)
  const [addMessage, setAddMessage] = useState('')

  const loadPool = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await coreApi<{ data: PoolData }>('/api/v1/nominations/pool')
      setData(res.data)
    } catch (err: any) {
      setError(err?.message || 'Unable to load pool')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadPins = useCallback(async () => {
    try {
      const res = await fetch(`${CORE_API}/api/v1/rally/pins?type=all`)
      if (!res.ok) return
      const json = await res.json()
      setRecentPins((json.data?.pins || []).slice(0, 5))
    } catch { /* ignore */ }
  }, [])

  useEffect(() => { loadPool(); loadPins() }, [loadPool, loadPins])

  const countdown = useCountdown(data?.upcoming_ceremony?.scheduled_at || null)

  const handleAddProspect = useCallback(async (e: FormEvent) => {
    e.preventDefault()
    if (!addName.trim()) return
    setAddSubmitting(true)
    setAddMessage('')
    try {
      await coreApi('/api/v1/nominations/pool/add-prospect', {
        method: 'POST',
        body: { prospect_name: addName.trim(), prospect_email: addEmail.trim() || undefined, sport: addSport.trim() || undefined },
      })
      setAddMessage('Prospect nominated! Confirmation email sent.')
      setAddName(''); setAddEmail(''); setAddSport('')
      setShowAddForm(false)
      await loadPool()
    } catch (err: any) {
      setAddMessage(err?.message || 'Failed to add prospect')
    } finally {
      setAddSubmitting(false)
    }
  }, [addName, addEmail, addSport, loadPool])

  if (error) {
    return <div className="rounded-xl bg-brick/10 px-4 py-3 text-sm text-brick">{error}</div>
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-cocoa border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Graduation banner — user has been drawn */}
      {data?.drawn_entity && (
        <div className="rounded-2xl border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 to-white p-6 shadow-md">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="mb-1 flex items-center gap-2">
                <span className="text-2xl">🎉</span>
                <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">You've been drawn!</p>
              </div>
              <h2 className="text-xl font-bold text-emerald-900">
                Your {data.drawn_entity.type === 'classroom' ? 'Classroom' : 'Locker Room'} is ready
              </h2>
              <p className="text-sm text-emerald-800/80">
                Head to your portal to set up your {data.drawn_entity.type === 'classroom' ? 'classroom' : 'locker room'} and start managing your resources.
              </p>
            </div>
            <Link
              to={data.drawn_entity.portal_path}
              className="rounded-xl bg-emerald-700 px-6 py-3 text-sm font-bold text-white shadow-md transition hover:bg-emerald-800"
            >
              Open {data.drawn_entity.type === 'classroom' ? 'Teacher' : 'Coach'} Portal →
            </Link>
          </div>
        </div>
      )}

      {/* Ceremony countdown hero */}
      {data?.upcoming_ceremony ? (
        <div className="rounded-2xl bg-gradient-to-br from-cocoa to-cocoa/80 p-6 text-white shadow-lg">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-white/60">Upcoming Ceremony</p>
              <h2 className="mt-1 text-2xl font-bold">
                {data.upcoming_ceremony.kind === 'classroom' ? 'Classroom' : 'Locker Room'} Draw
              </h2>
              <p className="mt-1 text-sm text-white/80">
                {formatDate(data.upcoming_ceremony.scheduled_at)} · {data.upcoming_ceremony.slots_available} slot{data.upcoming_ceremony.slots_available !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-white/60">Time remaining</p>
              <p className="text-3xl font-bold tabular-nums">{countdown}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-sand bg-white/70 p-5 text-center">
          <p className="text-sm text-cocoa/60">No ceremony scheduled yet. Keep rallying support — you'll get an email when a draw is scheduled.</p>
        </div>
      )}

      {/* Teacher: My nomination card */}
      {isTeacher && data?.my_nomination && (
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 space-y-3">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">Your Nomination</p>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-bold text-emerald-900">{data.my_nomination.name}</p>
              {data.my_nomination.detail && <p className="text-sm text-emerald-700">{data.my_nomination.detail}</p>}
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-emerald-900">{data.my_nomination.weight}</p>
              <p className="text-xs text-emerald-600">support weight</p>
            </div>
          </div>
          {data.my_nomination.support_code && (
            <div className="flex items-center gap-2 pt-1">
              <Link to="/portal/nominee/support" className="text-xs font-semibold text-emerald-700 underline">Open My Link →</Link>
            </div>
          )}
        </section>
      )}

      {/* Coach: My prospects */}
      {isCoach && (
        <section className="rounded-2xl bg-white/70 p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-cocoa">My Prospects</h2>
              <p className="text-xs text-cocoa/60">Prospects you've nominated — weight increases as supporters rally</p>
            </div>
            <button
              type="button"
              onClick={() => setShowAddForm(!showAddForm)}
              className="rounded-xl bg-cocoa px-4 py-2 text-xs font-semibold text-white transition hover:bg-cocoa/90"
            >
              {showAddForm ? 'Cancel' : '+ Nominate Another'}
            </button>
          </div>

          {showAddForm && (
            <form onSubmit={handleAddProspect} className="rounded-xl border border-dashed border-cocoa/30 bg-sand/20 p-4 space-y-3">
              <div className="grid gap-3 sm:grid-cols-3">
                <input className="rounded-xl border border-sand px-3 py-2 text-sm focus:border-cocoa focus:outline-none" value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Prospect name *" required />
                <input type="email" className="rounded-xl border border-sand px-3 py-2 text-sm focus:border-cocoa focus:outline-none" value={addEmail} onChange={(e) => setAddEmail(e.target.value)} placeholder="Prospect email" />
                <input className="rounded-xl border border-sand px-3 py-2 text-sm focus:border-cocoa focus:outline-none" value={addSport} onChange={(e) => setAddSport(e.target.value)} placeholder="Sport" />
              </div>
              <div className="flex items-center gap-3">
                <button type="submit" disabled={addSubmitting || !addName.trim()} className="rounded-xl bg-cocoa px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">
                  {addSubmitting ? 'Submitting...' : 'Nominate'}
                </button>
                {addMessage && <span className="text-xs text-cocoa/70">{addMessage}</span>}
              </div>
            </form>
          )}

          {(data?.my_prospects || []).length > 0 ? (
            <div className="space-y-2">
              {data!.my_prospects.map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-xl border border-sand/50 bg-white p-3">
                  <div>
                    <p className="text-sm font-semibold text-cocoa">{p.name}</p>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-cocoa/50">
                      {p.detail && <span>{p.detail}</span>}
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                        p.status === 'pool' ? 'bg-emerald-100 text-emerald-700' :
                        p.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                        p.status === 'drawn' ? 'bg-purple-100 text-purple-700' :
                        'bg-stone-100 text-stone-600'
                      }`}>
                        {p.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xl font-bold text-cocoa">{p.weight}</p>
                      <p className="text-[10px] text-cocoa/40">weight</p>
                    </div>
                    {p.support_code && (
                      <button
                        type="button"
                        className="rounded-lg border border-sand px-2 py-1 text-[10px] font-medium text-cocoa/70 hover:bg-sand/30"
                        onClick={() => navigator.clipboard.writeText(`${window.location.origin}/support/${p.support_code}`)}
                      >
                        Copy link
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-cocoa/50">No prospects nominated yet. Use the button above to add one.</p>
          )}
        </section>
      )}

      {/* Active pool + Rally preview */}
      <div className="grid gap-4 lg:grid-cols-[2fr,1fr]">
        <section className="rounded-2xl bg-white/70 p-5 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-cocoa">Active Pool</h2>
            <span className="text-xs text-cocoa/50">{data?.pool.length || 0} nominees</span>
          </div>
          {(data?.pool || []).length > 0 ? (
            <div className="space-y-2 max-h-[480px] overflow-y-auto">
              {data!.pool.map((n) => (
                <div key={`${n.type}-${n.id}`} className="flex items-center justify-between rounded-xl border border-sand/40 bg-white p-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-cocoa truncate">{n.name}</p>
                      <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                        n.type === 'teacher' ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
                      }`}>
                        {n.type}
                      </span>
                    </div>
                    {n.detail && <p className="text-xs text-cocoa/50 truncate">{n.detail}</p>}
                  </div>
                  <div className="ml-3 flex items-center gap-2">
                    <div className="text-right">
                      <p className="text-lg font-bold text-cocoa">{n.weight}</p>
                      <p className="text-[9px] text-cocoa/40">weight</p>
                    </div>
                    {n.support_code && (
                      <Link to={`/support/${n.support_code}`} className="rounded-lg border border-sand px-2 py-1 text-[10px] font-medium text-cocoa/70 hover:bg-sand/30">
                        Support
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-sand bg-sand/10 p-6 text-center">
              <p className="text-sm text-cocoa/50">No confirmed nominees in the pool yet.</p>
              <Link to="/nominate" className="mt-2 inline-block text-sm text-cocoa underline">Nominate someone</Link>
            </div>
          )}
        </section>

        <section className="rounded-2xl bg-white/70 p-5 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-cocoa">Rally Preview</h2>
            <Link to="/portal/nominee/rally" className="text-xs font-semibold text-cocoa/70 underline hover:text-cocoa">Open →</Link>
          </div>
          {recentPins.length > 0 ? (
            <div className="space-y-2 max-h-[380px] overflow-y-auto">
              {recentPins.map((pin) => (
                <div key={pin.id} className="rounded-xl border border-sand/40 bg-white p-3">
                  <p className="text-xs font-semibold text-cocoa">{pin.display_name}</p>
                  {pin.school_or_sport && <p className="text-[10px] text-cocoa/50">{pin.school_or_sport}</p>}
                  <p className="mt-1 text-xs italic text-cocoa/80 line-clamp-3">&ldquo;{pin.message}&rdquo;</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-sand bg-sand/10 p-4 text-center">
              <p className="text-xs text-cocoa/50">No pins yet. Be the first!</p>
            </div>
          )}
          <Link to="/portal/nominee/rally" className="block rounded-xl bg-cocoa px-4 py-2 text-center text-sm font-semibold text-white transition hover:bg-cocoa/90">
            Post a Pin
          </Link>
        </section>
      </div>
    </div>
  )
}
